// Mock data for the comprehensive Admin Dashboard
import { format, subDays, startOfMonth, endOfMonth, startOfDay, addDays } from 'date-fns';

export interface BatchRunData {
  id: string;
  batchId: string;
  date: Date;
  domain: string;
  program: string;
  chartType: string; // New field for chart type analysis
  totalProcessed: number;
  approved: number;
  sentForCorrection: number;
  billableUnits: number;
  rateUSD: number;
  minimumUSD: number;
  subtotalUSD: number;
  totalDueUSD: number;
  status: 'Complete' | 'Partial' | 'Failed';
  triggeredBy: 'User' | 'System';
  modelVersion: string;
  promptVersion: string;
  guardrailProfile: string;
  runDuration: number; // in minutes
  infraRegion: string;
  reviewerNotes?: string;
  items: BatchItem[];
  batchStatus:Object;
}

export interface BatchItem {
  id: string;
  chartId: string;
  visitId: string;
  outcome: 'Approved' | 'Sent for Correction';
  timestamp: Date;
  reviewer?: string;
  correctionReason?: string;
  correctionNote?: string;
  evidenceHash?: string;
}

export interface DomainProgram {
  domain: string;
  program: string;
  rateUSD: number;
  minimumUSD: number;
  effectiveFrom: Date;
  effectiveTo?: Date;
  isActive: boolean;
}

export interface ExceptionItem {
  id: string;
  type: 'Missing Rate' | 'Data Gap' | 'Anomaly' | 'Negative Value';
  domain: string;
  program: string;
  date: Date;
  description: string;
  severity: 'High' | 'Medium' | 'Low';
  status: 'Open' | 'In Progress' | 'Resolved';
  assignedTo?: string;
  dueDate?: Date;
  notes?: string;
}

// Generate mock batch run data
export const generateMockBatchData = (): BatchRunData[] => {
  const domains = ['Cardiology', 'Radiology', 'Emergency', 'Surgery', 'Internal Medicine'];
  const programs = ['Medicare', 'Medicaid', 'Commercial', 'Self Pay', 'VA'];
  const chartTypes = [
    'CSC - Section 28',
    'DLS - Section 17', 
    'RN',
    'Aide'
  ];
  const models = ['GPT-4-Turbo-v1.2', 'Claude-3-Opus-v2.1', 'Gemini-Pro-v1.5'];
  const prompts = ['Clinical-Audit-v3.2', 'Billing-Review-v2.8', 'Quality-Check-v4.1'];
  const regions = ['US-East-1', 'US-West-2', 'EU-Central-1'];

  const data: BatchRunData[] = [];

  // Generate data for the last 90 days
  for (let i = 0; i < 90; i++) {
    const date = subDays(new Date(), i);
    const numBatches = Math.floor(Math.random() * 5) + 2; // 2-6 batches per day

    for (let j = 0; j < numBatches; j++) {
      const domain = domains[Math.floor(Math.random() * domains.length)];
      const program = programs[Math.floor(Math.random() * programs.length)];
      const chartType = chartTypes[Math.floor(Math.random() * chartTypes.length)];
      const totalProcessed = Math.floor(Math.random() * 500) + 50;
      const approvalRate = 0.75 + Math.random() * 0.2; // 75-95% approval rate
      const approved = Math.floor(totalProcessed * approvalRate);
      const sentForCorrection = totalProcessed - approved;
      
      // Billing calculations
      const baseRate = 2.5 + Math.random() * 7.5; // $2.5-$10 per unit
      const rateUSD = Math.round(baseRate * 100) / 100;
      const minimumUSD = Math.round((rateUSD * 10) * 100) / 100; // Minimum is 10 units worth
      const billableUnits = approved; // Only approved charts are billable
      const subtotalUSD = Math.round((billableUnits * rateUSD) * 100) / 100;
      const totalDueUSD = Math.max(subtotalUSD, minimumUSD);

      const items: BatchItem[] = [];
      for (let k = 0; k < totalProcessed; k++) {
        const outcome = k < approved ? 'Approved' : 'Sent for Correction';
        items.push({
          id: `item_${i}_${j}_${k}`,
          chartId: `CHART_${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
          visitId: `VISIT_${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
          outcome,
          timestamp: new Date(date.getTime() + Math.random() * 24 * 60 * 60 * 1000),
          reviewer: outcome === 'Sent for Correction' ? `Dr. ${['Smith', 'Johnson', 'Williams', 'Brown'][Math.floor(Math.random() * 4)]}` : undefined,
          correctionReason: outcome === 'Sent for Correction' ? ['Incomplete Documentation', 'Coding Error', 'Missing Diagnosis', 'Billing Mismatch'][Math.floor(Math.random() * 4)] : undefined,
          correctionNote: outcome === 'Sent for Correction' ? 'Requires additional review and correction' : undefined,
          evidenceHash: `sha256_${Math.random().toString(36).substr(2, 16)}`
        });
      }

      data.push({
        id: `batch_${i}_${j}`,
        batchId: `BATCH_${format(date, 'yyyyMMdd')}_${j.toString().padStart(3, '0')}`,
        date,
        domain,
        program,
        chartType,
        totalProcessed,
        approved,
        sentForCorrection,
        billableUnits,
        rateUSD,
        minimumUSD,
        subtotalUSD,
        totalDueUSD,
        status: Math.random() > 0.95 ? (Math.random() > 0.5 ? 'Partial' : 'Failed') : 'Complete',
        triggeredBy: Math.random() > 0.3 ? 'System' : 'User',
        modelVersion: models[Math.floor(Math.random() * models.length)],
        promptVersion: prompts[Math.floor(Math.random() * prompts.length)],
        guardrailProfile: 'Healthcare-Standard-v2.1',
        runDuration: Math.floor(Math.random() * 45) + 15, // 15-60 minutes
        infraRegion: regions[Math.floor(Math.random() * regions.length)],
        reviewerNotes: Math.random() > 0.8 ? 'Batch completed with minor issues resolved' : undefined,
        items
      });
    }
  }

  return data.sort((a, b) => b.date.getTime() - a.date.getTime());
};

// Rate card data
export const generateRateCardData = (): DomainProgram[] => {
  const domains = ['Cardiology', 'Radiology', 'Emergency', 'Surgery', 'Internal Medicine'];
  const programs = ['Medicare', 'Medicaid', 'Commercial', 'Self Pay', 'VA'];
  
  const rateCard: DomainProgram[] = [];
  
  domains.forEach(domain => {
    programs.forEach(program => {
      const baseRate = 2.5 + Math.random() * 7.5;
      rateCard.push({
        domain,
        program,
        rateUSD: Math.round(baseRate * 100) / 100,
        minimumUSD: Math.round((baseRate * 10) * 100) / 100,
        effectiveFrom: subDays(new Date(), 180),
        isActive: Math.random() > 0.1 // 90% active
      });
    });
  });
  
  return rateCard;
};

// Exception data
export const generateExceptionData = (): ExceptionItem[] => {
  const exceptions: ExceptionItem[] = [];
  const domains = ['Cardiology', 'Radiology', 'Emergency', 'Surgery', 'Internal Medicine'];
  const programs = ['Medicare', 'Medicaid', 'Commercial', 'Self Pay', 'VA'];
  
  // Generate some exceptions
  for (let i = 0; i < 15; i++) {
    const types: ExceptionItem['type'][] = ['Missing Rate', 'Data Gap', 'Anomaly', 'Negative Value'];
    const type = types[Math.floor(Math.random() * types.length)];
    
    exceptions.push({
      id: `exception_${i}`,
      type,
      domain: domains[Math.floor(Math.random() * domains.length)],
      program: programs[Math.floor(Math.random() * programs.length)],
      date: subDays(new Date(), Math.floor(Math.random() * 30)),
      description: getExceptionDescription(type),
      severity: ['High', 'Medium', 'Low'][Math.floor(Math.random() * 3)] as 'High' | 'Medium' | 'Low',
      status: ['Open', 'In Progress', 'Resolved'][Math.floor(Math.random() * 3)] as 'Open' | 'In Progress' | 'Resolved',
      assignedTo: Math.random() > 0.3 ? `${['Admin', 'Ops Team', 'Finance'][Math.floor(Math.random() * 3)]}` : undefined,
      dueDate: Math.random() > 0.5 ? addDays(new Date(), Math.floor(Math.random() * 14) + 1) : undefined,
      notes: Math.random() > 0.6 ? 'Under investigation' : undefined
    });
  }
  
  return exceptions.sort((a, b) => b.date.getTime() - a.date.getTime());
};

function getExceptionDescription(type: ExceptionItem['type']): string {
  switch (type) {
    case 'Missing Rate':
      return 'Rate_USD not configured for this domain/program combination';
    case 'Data Gap':
      return 'Total_Processed < Approved + Sent_For_Correction';
    case 'Anomaly':
      return 'Approval rate dropped below 3σ threshold';
    case 'Negative Value':
      return 'Negative or null billable units detected';
    default:
      return 'Unknown exception type';
  }
}

// Generate all mock data
export const mockBatchData = generateMockBatchData();
export const mockRateCardData = generateRateCardData();
export const mockExceptionData = generateExceptionData();

// Helper functions for dashboard calculations
export const calculateKPIs = (data: BatchRunData[], dateRange?: { from: Date; to: Date }) => {
  let filteredData = data;
  
  if (dateRange) {
    filteredData = data.filter(batch => 
      batch.date >= dateRange.from && batch.date <= dateRange.to
    );
  }
  
  const totalProcessed = filteredData.reduce((sum, batch) => sum + batch.totalProcessed, 0);
  const approved = filteredData.reduce((sum, batch) => sum + batch.approved, 0);
  const sentForCorrection = filteredData.reduce((sum, batch) => sum + batch.sentForCorrection, 0);
  const approvalRate = totalProcessed > 0 ? (approved / totalProcessed) * 100 : 0;
  // Keep billing fields for backwards compatibility but focus on chart review metrics
  const billableUnits = filteredData.reduce((sum, batch) => sum + batch.billableUnits, 0);
  const revenue = filteredData.reduce((sum, batch) => sum + batch.totalDueUSD, 0);
  
  return {
    totalProcessed,
    approved,
    sentForCorrection,
    approvalRate,
    billableUnits,
    revenue
  };
};

export const getTimeSeriesData = (data: BatchRunData[], groupBy: 'day' | 'week' | 'month' = 'day') => {
  const grouped = new Map();
  
  data.forEach(batch => {
    let key: string;
    switch (groupBy) {
      case 'day':
        key = format(batch.date, 'yyyy-MM-dd');
        break;
      case 'week':
        key = format(batch.date, 'yyyy-ww');
        break;
      case 'month':
        key = format(batch.date, 'yyyy-MM');
        break;
    }
    
    if (!grouped.has(key)) {
      grouped.set(key, {
        date: key,
        totalProcessed: 0,
        approved: 0,
        sentForCorrection: 0,
        approvalRate: 0,
        // AI vs Auditor metrics
        aiApproved: 0,
        auditorApproved: 0,
        aiProcessed: 0,
        auditorProcessed: 0,
        aiCorrections: 0,
        auditorCorrections: 0,
        aiApprovalRate: 0,
        auditorApprovalRate: 0,
        // Keep billing fields for backwards compatibility
        billableUnits: 0,
        revenue: 0
      });
    }
    
    const item = grouped.get(key);
    item.totalProcessed += batch.totalProcessed;
    item.approved += batch.approved;
    item.sentForCorrection += batch.sentForCorrection;
    item.billableUnits += batch.billableUnits;
    item.revenue += batch.totalDueUSD;
    
    // Simulate AI vs Auditor split (AI handles ~70% of volume)
    const aiShare = 0.65 + Math.random() * 0.2; // 65-85% AI processed
    const aiProcessedCount = Math.floor(batch.totalProcessed * aiShare);
    const auditorProcessedCount = batch.totalProcessed - aiProcessedCount;
    
    // AI typically has higher approval rate (85-95%) vs Auditor (70-85%)
    const aiApprovalRate = 0.85 + Math.random() * 0.1;
    const auditorApprovalRate = 0.70 + Math.random() * 0.15;
    
    const aiApprovedCount = Math.floor(aiProcessedCount * aiApprovalRate);
    const auditorApprovedCount = Math.floor(auditorProcessedCount * auditorApprovalRate);
    
    item.aiProcessed += aiProcessedCount;
    item.auditorProcessed += auditorProcessedCount;
    item.aiApproved += aiApprovedCount;
    item.auditorApproved += auditorApprovedCount;
    
    // Calculate corrections
    item.aiCorrections = (item.aiCorrections || 0) + (aiProcessedCount - aiApprovedCount);
    item.auditorCorrections = (item.auditorCorrections || 0) + (auditorProcessedCount - auditorApprovedCount);
  });
  
  return Array.from(grouped.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(item => ({
      ...item,
      // Ensure all values are numbers and not undefined/null
      totalProcessed: Number(item.totalProcessed) || 0,
      approved: Number(item.approved) || 0,
      sentForCorrection: Number(item.sentForCorrection) || 0,
      approvalRate: item.totalProcessed > 0 ? (item.approved / item.totalProcessed) * 100 : 0,
      // AI vs Auditor metrics
      aiApproved: Number(item.aiApproved) || 0,
      auditorApproved: Number(item.auditorApproved) || 0,
      aiProcessed: Number(item.aiProcessed) || 0,
      auditorProcessed: Number(item.auditorProcessed) || 0,
      aiCorrections: Number(item.aiCorrections) || 0,
      auditorCorrections: Number(item.auditorCorrections) || 0,
      aiApprovalRate: item.aiProcessed > 0 ? (item.aiApproved / item.aiProcessed) * 100 : 0,
      auditorApprovalRate: item.auditorProcessed > 0 ? (item.auditorApproved / item.auditorProcessed) * 100 : 0,
      billableUnits: Number(item.billableUnits) || 0,
      revenue: Number(item.revenue) || 0
    }));
};